import React, { useMemo } from "react";
import { Calendar, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const parseCdrDateTime = (value) => {
  if (!value || value === "-") return null;

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  const match = String(value).match(/(\d{2})\.(\d{2})\.(\d{4})[,\s]+(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  return new Date(+match[3], +match[2] - 1, +match[1], +match[4], +match[5], +(match[6] || 0));
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const pad = (n) => String(n).padStart(2, "0");

const formatFullDateTime = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

const CdrDateTimeCell = ({ value, isDark }) => {
  const formatted = useMemo(() => {
    const date = parseCdrDateTime(value);
    if (!date) return null;

    const today = new Date();
    const isToday = isSameDay(date, today);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = isSameDay(date, yesterday);

    let dateLabel = date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
    if (isToday) dateLabel = "Bugün";
    else if (isYesterday) dateLabel = "Dün";

    const timeLabel = date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

    return {
      dateLabel,
      timeLabel,
      fullLabel: formatFullDateTime(date),
      isToday,
    };
  }, [value]);

  if (!formatted) {
    return <span className={isDark ? "text-slate-500" : "text-gray-400"}>-</span>;
  }

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-md border text-[11px] tabular-nums cursor-default",
            formatted.isToday
              ? (isDark ? "border-indigo-500/30 bg-indigo-500/10" : "border-indigo-200 bg-indigo-50/80")
              : (isDark ? "border-slate-700/70 bg-slate-800/50" : "border-slate-200 bg-slate-50/90")
          )}
        >
          <span className="inline-flex items-center gap-0.5">
            <Calendar className={cn("w-3 h-3 shrink-0", formatted.isToday ? "text-indigo-400" : (isDark ? "text-slate-500" : "text-slate-400"))} />
            <span className={cn("font-medium", formatted.isToday ? (isDark ? "text-indigo-300" : "text-indigo-700") : (isDark ? "text-slate-300" : "text-slate-700"))}>
              {formatted.dateLabel}
            </span>
          </span>
          <span className={cn("inline-flex items-center gap-0.5", isDark ? "text-violet-300" : "text-violet-700")}>
            <Clock className={cn("w-3 h-3 shrink-0", isDark ? "text-violet-400" : "text-violet-500")} />
            <span className="font-mono">{formatted.timeLabel}</span>
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className={isDark ? "bg-slate-900 border-slate-700 text-slate-100 font-mono text-xs" : "font-mono text-xs"}>
        {formatted.fullLabel}
      </TooltipContent>
    </Tooltip>
  );
};

export default CdrDateTimeCell;
